import React, { useCallback, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import '../DmsDialogChrome.css';
import './ConfirmDialog.css';

export type ConfirmDialogMode = 'yesNo' | 'default';
export type ConfirmDialogIcon = 'warning' | 'error' | 'success' | 'info';
export type ConfirmDialogSeverity = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmDialogProps {
    visible: boolean;
    onHide: () => void;
    mode: ConfirmDialogMode;
    title: string;
    message: string;
    icon?: ConfirmDialogIcon;
    onConfirm?: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    severity?: ConfirmDialogSeverity;
    dismissableMask?: boolean;
    closeOnEscape?: boolean;
    closable?: boolean;
}

const iconClassMap: Record<ConfirmDialogIcon, string> = {
    warning: 'pi pi-exclamation-triangle',
    error: 'pi pi-times-circle',
    success: 'pi pi-check-circle',
    info: 'pi pi-info-circle',
};

const severityClassMap: Record<ConfirmDialogSeverity, string> = {
    danger: 'confirm-dialog__badge--danger',
    warning: 'confirm-dialog__badge--warning',
    info: 'confirm-dialog__badge--info',
    success: 'confirm-dialog__badge--success',
};

const inferSeverity = (
    icon: ConfirmDialogIcon | undefined,
    severity: ConfirmDialogSeverity | undefined
): ConfirmDialogSeverity => {
    if (severity) {
        return severity;
    }

    if (icon === 'error') {
        return 'danger';
    }

    return icon ?? 'info';
};

const ConfirmDialog = ({
    visible,
    onHide,
    mode,
    title,
    message,
    icon = 'info',
    onConfirm,
    confirmLabel,
    cancelLabel,
    severity,
    dismissableMask,
    closeOnEscape,
    closable,
}: ConfirmDialogProps) => {
    const { t } = useTranslation();
    const resolvedSeverity = useMemo(() => inferSeverity(icon, severity), [icon, severity]);
    const resolvedConfirmLabel = confirmLabel ?? (mode === 'yesNo' ? t('dialog.yes') : t('dialog.ok'));
    const resolvedCancelLabel = cancelLabel ?? t('dialog.cancel');

    const isBlockingMode = mode === 'yesNo';
    const resolvedDismissableMask = dismissableMask ?? !isBlockingMode;
    const resolvedCloseOnEscape = closeOnEscape ?? true;
    const resolvedClosable = closable ?? true;

    const triggerConfirm = useCallback(() => {
        if (onConfirm) {
            onConfirm();
            return;
        }

        onHide();
    }, [onConfirm, onHide]);

    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (!resolvedCloseOnEscape) {
                    return;
                }

                event.preventDefault();
                onHide();
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                if (isBlockingMode) {
                    triggerConfirm();
                    return;
                }

                onHide();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [visible, isBlockingMode, onHide, resolvedCloseOnEscape, triggerConfirm]);


    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            closable={resolvedClosable}
            closeOnEscape={resolvedCloseOnEscape}
            dismissableMask={resolvedDismissableMask}
            draggable={false}
            resizable={false}
            showHeader={false}
            className="confirm-dialog dms-dialog"
            contentClassName="confirm-dialog__content dms-dialog__content"
        >
            <div className="confirm-dialog__header dms-dialog__header">
                <div className="confirm-dialog__title-wrap">
                    <h2 className="confirm-dialog__title dms-dialog__title">{title}</h2>
                </div>
                {resolvedClosable ? (
                    <button
                        type="button"
                        className="confirm-dialog__close dms-dialog__close"
                        onClick={onHide}
                        aria-label={t('dialog.close', { defaultValue: 'Close' })}
                    >
                        <i className="pi pi-times" aria-hidden="true" />
                    </button>
                ) : null}
            </div>
            <div className="confirm-dialog__body">
                <span
                    className={`confirm-dialog__badge ${severityClassMap[resolvedSeverity]}`}
                    aria-hidden="true"
                    data-testid="confirm-dialog-icon"
                >
                    <i className={iconClassMap[icon]} />
                </span>

                <div className="confirm-dialog__copy">
                    <p className="confirm-dialog__message">{message}</p>
                </div>
            </div>

            <div className="confirm-dialog__footer">
                <div className="confirm-dialog__actions">
                    {mode === 'yesNo' && (
                        <Button
                            type="button"
                            label={resolvedCancelLabel}
                            className="p-button-text confirm-dialog__button confirm-dialog__button--secondary"
                            onClick={onHide}
                        />
                    )}
                    <Button
                        type="button"
                        autoFocus
                        label={resolvedConfirmLabel}
                        className={`confirm-dialog__button confirm-dialog__button--primary confirm-dialog__button--${resolvedSeverity}`}
                        onClick={triggerConfirm}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default ConfirmDialog;
